import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, TextField, Button, Typography,
  FormControl, InputLabel, Select, MenuItem, Grid, Alert
} from '@mui/material';
import { Save, ArrowBack, Publish } from '@mui/icons-material';
import { getArticle, createArticle, updateArticle, getCategories } from '../services/api';

function ArticleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [article, setArticle] = useState({
    title: '', slug: '', content: '', excerpt: '', articleType: 'actualite',
    category: '', status: 'draft', tags: [], metaTitle: '', metaDescription: ''
  });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const catRes = await getCategories();
      setCategories(catRes.data);
      if (!isNew) {
        const { data } = await getArticle(id);
        setArticle({ ...data, category: data.category?._id || '', tags: data.tags || [] });
      }
    } catch (error) {
      setError('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setArticle({ ...article, [field]: value });
    if (field === 'title' && isNew) {
      const slug = value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      setArticle(prev => ({ ...prev, slug }));
    }
  };

  const handleSave = async (publish = false) => {
    setError(''); setSuccess(''); setSaving(true);
    try {
      const data = { ...article };
      if (publish) data.status = 'published';
      if (!data.category) delete data.category;

      if (isNew) {
        const { data: newArticle } = await createArticle(data);
        setSuccess('Article cree');
        navigate(`/articles/${newArticle._id}`);
      } else {
        await updateArticle(id, data);
        setSuccess('Article mis a jour');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Typography>Chargement...</Typography>;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/articles')}>Retour</Button>
          <Typography variant="h4" fontWeight="bold">{isNew ? 'Nouvel article' : 'Modifier'}</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" startIcon={<Save />} onClick={() => handleSave(false)} disabled={saving}>Enregistrer</Button>
          <Button variant="contained" startIcon={<Publish />} onClick={() => handleSave(true)} disabled={saving}>Publier</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <TextField fullWidth label="Titre" value={article.title} onChange={(e) => handleChange('title', e.target.value)} margin="normal" required />
              <TextField fullWidth label="Slug" value={article.slug} onChange={(e) => handleChange('slug', e.target.value)} margin="normal" />
              <TextField fullWidth label="Extrait" value={article.excerpt} onChange={(e) => handleChange('excerpt', e.target.value)} margin="normal" multiline rows={2} />
              <TextField fullWidth label="Contenu" value={article.content} onChange={(e) => handleChange('content', e.target.value)} margin="normal" multiline rows={15} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>Publication</Typography>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select value={article.articleType} label="Type" onChange={(e) => handleChange('articleType', e.target.value)}>
                  <MenuItem value="actualite">Actualite</MenuItem>
                  <MenuItem value="recherche">Recherche</MenuItem>
                  <MenuItem value="conseil">Conseil</MenuItem>
                  <MenuItem value="evenement">Evenement</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Statut</InputLabel>
                <Select value={article.status} label="Statut" onChange={(e) => handleChange('status', e.target.value)}>
                  <MenuItem value="draft">Brouillon</MenuItem>
                  <MenuItem value="published">Publie</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel>Categorie</InputLabel>
                <Select value={article.category} label="Categorie" onChange={(e) => handleChange('category', e.target.value)}>
                  <MenuItem value="">Aucune</MenuItem>
                  {categories.map((cat) => (<MenuItem key={cat._id} value={cat._id}>{cat.name}</MenuItem>))}
                </Select>
              </FormControl>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ArticleEditor;
